import {
  LocalClusteredMarkers,
  MapClusterSummary,
  type MapClusterSummaryState,
} from "@/components/map/map-count-cluster";
import {
  MAP_ZOOM_GESTURE_HANDLING,
  MapZoomHint,
} from "@/components/map/map-zoom-hint";
import {
  INSPECTION_MAP_DEFAULT_CENTER,
  INSPECTION_MAP_DEFAULT_ZOOM,
  INSPECTION_MAP_MAX_ZOOM,
  useInspectionMapFitBounds,
} from "@/hooks/use-inspection-map-bounds";
import {
  type InspectionPortalLicenseItem,
  formatInspectionAddress,
} from "@/lib/inspection-portal-api";
import { cn } from "@/lib/utils";
import type { MapCenter } from "@/types/map";
import {
  AdvancedMarker,
  Map as GoogleMap,
  InfoWindow,
  Pin,
  useMap,
  useMapsLibrary,
} from "@vis.gl/react-google-maps";
import { BoxSelect, LassoSelect, MousePointerClick } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button } from "../ui/button";
import { Tabs, TabsList, TabsTrigger } from "../ui/tabs";

type MappableLicense = InspectionPortalLicenseItem & {
  latitude: number;
  longitude: number;
  lat: number;
  lng: number;
};

type SelectionMode = "click" | "rectangle" | "lasso";

interface InspectionDispatchMapProps {
  licenses: InspectionPortalLicenseItem[];
  selectedIds: Set<string>;
  onSelectedIdsChange: (ids: Set<string>) => void;
  className?: string;
}

export function InspectionDispatchMap({
  licenses,
  selectedIds,
  onSelectedIdsChange,
  className,
}: InspectionDispatchMapProps) {
  const [mode, setMode] = useState<SelectionMode>("click");
  const [lassoPointCount, setLassoPointCount] = useState(0);
  const completeLassoRef = useRef<(() => void) | null>(null);
  const isDrawingMode = mode === "rectangle" || mode === "lasso";
  const pins = useMemo(
    () =>
      licenses
        .filter(
          (
            item,
          ): item is InspectionPortalLicenseItem & {
            latitude: number;
            longitude: number;
          } => item.latitude != null && item.longitude != null,
        )
        .map((item) => ({
          ...item,
          lat: item.latitude,
          lng: item.longitude,
        })),
    [licenses],
  );
  const [clusterSummary, setClusterSummary] =
    useState<MapClusterSummaryState | null>(null);
  const handleClusterSummary = useCallback(
    (summary: MapClusterSummaryState) => {
      setClusterSummary(summary);
    },
    [],
  );

  const mapCenter = useMemo<MapCenter>(() => {
    if (pins.length === 0) return INSPECTION_MAP_DEFAULT_CENTER;
    const lats = pins.map((p) => p.latitude);
    const lngs = pins.map((p) => p.longitude);
    return {
      lat: (Math.min(...lats) + Math.max(...lats)) / 2,
      lng: (Math.min(...lngs) + Math.max(...lngs)) / 2,
    };
  }, [pins]);

  const togglePin = useCallback(
    (licenseId: string) => {
      const next = new Set(selectedIds);
      if (next.has(licenseId)) next.delete(licenseId);
      else next.add(licenseId);
      onSelectedIdsChange(next);
    },
    [onSelectedIdsChange, selectedIds],
  );

  const selectPins = useCallback(
    (licenseIds: string[], additive: boolean) => {
      const next = additive ? new Set(selectedIds) : new Set<string>();
      for (const id of licenseIds) next.add(id);
      onSelectedIdsChange(next);
    },
    [onSelectedIdsChange, selectedIds],
  );

  return (
    <div className="flex flex-col gap-2">
      <Tabs
        value={mode}
        onValueChange={(value) => setMode(value as SelectionMode)}
      >
        <TabsList className="bg-neutral-100">
          <TabsTrigger
            value="click"
            aria-label="Click to select"
            title="Click to select"
            className="px-2.5"
          >
            <MousePointerClick aria-hidden="true" />
            <span className="sr-only">Click</span>
          </TabsTrigger>
          <TabsTrigger
            value="rectangle"
            aria-label="Rectangle select"
            title="Rectangle select"
            className="px-2.5"
          >
            <BoxSelect aria-hidden="true" />
            <span className="sr-only">Rectangle</span>
          </TabsTrigger>
          <TabsTrigger
            value="lasso"
            aria-label="Lasso select"
            title="Lasso select"
            className="px-2.5"
          >
            <LassoSelect aria-hidden="true" />
            <span className="sr-only">Lasso</span>
          </TabsTrigger>
        </TabsList>
      </Tabs>
      <div
        className={cn(
          "relative",
          className ??
            "h-[calc(100dvh-8rem)] min-h-[24rem] w-full overflow-hidden rounded-lg",
        )}
      >
        <MapZoomHint />
        <MapClusterSummary
          mode={clusterSummary?.mode ?? "pins"}
          totalInView={clusterSummary?.totalInView ?? pins.length}
          clusterCount={clusterSummary?.clusterCount ?? 0}
        />
        <GoogleMap
          mapId="inspection-dispatch-map"
          defaultCenter={mapCenter}
          defaultZoom={INSPECTION_MAP_DEFAULT_ZOOM}
          maxZoom={INSPECTION_MAP_MAX_ZOOM}
          // Disable map drag while drawing so mousedown/move draws a selection
          // instead of panning the viewport.
          draggable={!isDrawingMode}
          gestureHandling={MAP_ZOOM_GESTURE_HANDLING}
          disableDoubleClickZoom={mode === "lasso"}
          disableDefaultUI={false}
          className="h-full w-full"
        >
          <DispatchMapBounds pins={pins} />
          <LocalClusteredMarkers
            points={pins}
            onSummary={handleClusterSummary}
            renderPoint={(pin) => (
              <DispatchPropertyMarker
                key={pin.license_id}
                pin={pin}
                selected={selectedIds.has(pin.license_id)}
                clickToSelect={mode === "click"}
                onToggle={() => togglePin(pin.license_id)}
              />
            )}
          />
          {mode === "rectangle" ? (
            <RectangleSelector pins={pins} onSelect={selectPins} />
          ) : null}
          {mode === "lasso" ? (
            <LassoSelector
              pins={pins}
              onSelect={selectPins}
              onPointCountChange={setLassoPointCount}
              onRegisterComplete={(fn) => {
                completeLassoRef.current = fn;
              }}
            />
          ) : null}
        </GoogleMap>
      </div>
      {mode === "lasso" ? (
        <Button
          type="button"
          size="sm"
          variant="secondary"
          disabled={lassoPointCount < 3}
          onClick={() => completeLassoRef.current?.()}
        >
          Complete lasso ({lassoPointCount} points)
        </Button>
      ) : null}
    </div>
  );
}

function DispatchPropertyMarker({
  pin,
  selected,
  clickToSelect,
  onToggle,
}: {
  pin: MappableLicense;
  selected: boolean;
  clickToSelect: boolean;
  onToggle: () => void;
}) {
  const [showInfoWindow, setShowInfoWindow] = useState(false);
  const address = formatInspectionAddress(pin);

  return (
    <>
      <AdvancedMarker
        position={{ lat: pin.latitude, lng: pin.longitude }}
        title={address}
        onClick={() => {
          if (clickToSelect) onToggle();
        }}
        onMouseEnter={() => setShowInfoWindow(true)}
        onMouseLeave={() => setShowInfoWindow(false)}
      >
        <Pin
          background={selected ? "#2563eb" : "#111111"}
          borderColor={selected ? "#1d4ed8" : "#000000"}
          glyphColor="#ffffff"
        />
      </AdvancedMarker>
      {showInfoWindow ? (
        <InfoWindow
          position={{ lat: pin.latitude, lng: pin.longitude }}
          disableAutoPan
          headerDisabled
        >
          <div className="max-w-xs space-y-1 text-sm">
            {pin.display_id ? (
              <p className="font-medium">{pin.display_id}</p>
            ) : null}
            {pin.jurisdiction_name ? (
              <p className="text-muted-foreground">{pin.jurisdiction_name}</p>
            ) : null}
            <p>{address}</p>
          </div>
        </InfoWindow>
      ) : null}
    </>
  );
}

function DispatchMapBounds({ pins }: { pins: MappableLicense[] }) {
  useInspectionMapFitBounds(pins);
  return null;
}

function RectangleSelector({
  pins,
  onSelect,
}: {
  pins: MappableLicense[];
  onSelect: (licenseIds: string[], additive: boolean) => void;
}) {
  const map = useMap();
  const rectangleRef = useRef<google.maps.Rectangle | null>(null);
  const startRef = useRef<google.maps.LatLngLiteral | null>(null);
  const draggingRef = useRef(false);

  useEffect(() => {
    if (!map) return;

    const clearRectangle = () => {
      rectangleRef.current?.setMap(null);
      rectangleRef.current = null;
    };

    const onMouseDown = (event: google.maps.MapMouseEvent) => {
      if (!event.latLng) return;
      draggingRef.current = true;
      startRef.current = { lat: event.latLng.lat(), lng: event.latLng.lng() };
      clearRectangle();
      rectangleRef.current = new google.maps.Rectangle({
        map,
        bounds: {
          north: startRef.current.lat,
          south: startRef.current.lat,
          east: startRef.current.lng,
          west: startRef.current.lng,
        },
        strokeColor: "#2563eb",
        strokeOpacity: 0.9,
        strokeWeight: 2,
        fillColor: "#2563eb",
        fillOpacity: 0.15,
        clickable: false,
      });
    };

    const onMouseMove = (event: google.maps.MapMouseEvent) => {
      if (!draggingRef.current || !startRef.current || !event.latLng) return;
      const lat = event.latLng.lat();
      const lng = event.latLng.lng();
      rectangleRef.current?.setBounds({
        north: Math.max(startRef.current.lat, lat),
        south: Math.min(startRef.current.lat, lat),
        east: Math.max(startRef.current.lng, lng),
        west: Math.min(startRef.current.lng, lng),
      });
    };

    const finishDrag = (shiftKey: boolean) => {
      if (!draggingRef.current || !startRef.current) return;
      draggingRef.current = false;
      const bounds = rectangleRef.current?.getBounds();
      startRef.current = null;
      if (!bounds) {
        clearRectangle();
        return;
      }
      const selected = pins
        .filter((pin) =>
          bounds.contains({ lat: pin.latitude, lng: pin.longitude }),
        )
        .map((pin) => pin.license_id);
      onSelect(selected, shiftKey);
      clearRectangle();
    };

    const onMouseUp = (event: google.maps.MapMouseEvent) => {
      finishDrag(
        Boolean(event.domEvent && (event.domEvent as MouseEvent).shiftKey),
      );
    };

    // If the pointer is released outside the map, still complete the selection.
    const onWindowMouseUp = (event: MouseEvent) => {
      finishDrag(event.shiftKey);
    };

    const downListener = map.addListener("mousedown", onMouseDown);
    const moveListener = map.addListener("mousemove", onMouseMove);
    const upListener = map.addListener("mouseup", onMouseUp);
    window.addEventListener("mouseup", onWindowMouseUp);

    return () => {
      google.maps.event.removeListener(downListener);
      google.maps.event.removeListener(moveListener);
      google.maps.event.removeListener(upListener);
      window.removeEventListener("mouseup", onWindowMouseUp);
      clearRectangle();
    };
  }, [map, onSelect, pins]);

  return null;
}

function LassoSelector({
  pins,
  onSelect,
  onPointCountChange,
  onRegisterComplete,
}: {
  pins: MappableLicense[];
  onSelect: (licenseIds: string[], additive: boolean) => void;
  onPointCountChange: (count: number) => void;
  onRegisterComplete: (fn: () => void) => void;
}) {
  const map = useMap();
  const geometry = useMapsLibrary("geometry");
  const [points, setPoints] = useState<google.maps.LatLngLiteral[]>([]);
  const polylineRef = useRef<google.maps.Polyline | null>(null);
  const polygonRef = useRef<google.maps.Polygon | null>(null);

  const clearOverlays = useCallback(() => {
    polylineRef.current?.setMap(null);
    polygonRef.current?.setMap(null);
    polylineRef.current = null;
    polygonRef.current = null;
  }, []);

  const completeLasso = useCallback(
    (path: google.maps.LatLngLiteral[], shiftKey: boolean) => {
      if (!geometry || path.length < 3) {
        setPoints([]);
        clearOverlays();
        return;
      }
      const polygon = new google.maps.Polygon({ paths: path });
      const selected = pins
        .filter((pin) =>
          geometry.poly.containsLocation(
            { lat: pin.latitude, lng: pin.longitude },
            polygon,
          ),
        )
        .map((pin) => pin.license_id);
      onSelect(selected, shiftKey);
      setPoints([]);
      clearOverlays();
    },
    [clearOverlays, geometry, onSelect, pins],
  );

  useEffect(() => {
    onPointCountChange(points.length);
  }, [onPointCountChange, points.length]);

  useEffect(() => {
    onRegisterComplete(() => completeLasso(points, false));
  }, [completeLasso, onRegisterComplete, points]);

  useEffect(() => {
    if (!map) return;

    const onClick = (event: google.maps.MapMouseEvent) => {
      const latLng = event.latLng;
      if (!latLng) return;
      setPoints((prev) => [...prev, { lat: latLng.lat(), lng: latLng.lng() }]);
    };

    const onDblClick = (event: google.maps.MapMouseEvent) => {
      event.stop();
      setPoints((prev) => {
        if (prev.length >= 3) {
          completeLasso(
            prev,
            Boolean(event.domEvent && (event.domEvent as MouseEvent).shiftKey),
          );
        }
        return [];
      });
    };

    const clickListener = map.addListener("click", onClick);
    const dblListener = map.addListener("dblclick", onDblClick);
    return () => {
      google.maps.event.removeListener(clickListener);
      google.maps.event.removeListener(dblListener);
    };
  }, [completeLasso, map]);

  useEffect(() => {
    if (!map) return;
    clearOverlays();
    if (points.length === 0) return;

    if (points.length >= 2) {
      polylineRef.current = new google.maps.Polyline({
        map,
        path: points,
        strokeColor: "#2563eb",
        strokeOpacity: 0.9,
        strokeWeight: 2,
        clickable: false,
      });
    }
    if (points.length >= 3) {
      polygonRef.current = new google.maps.Polygon({
        map,
        paths: points,
        strokeColor: "#2563eb",
        strokeOpacity: 0.9,
        strokeWeight: 2,
        fillColor: "#2563eb",
        fillOpacity: 0.15,
        clickable: false,
      });
    }
  }, [clearOverlays, map, points]);

  return null;
}
